import {_, currencyToSymbolMap, Keys, lockr, React, Highlighter, classNames} from '@api'
import {CustomAttributeType} from '@api/graphql'
import {BuilderCardFieldStore, dataMapperConfig, ISidebarDetailsStore, ISidebarWhereClause, SidebarAlign, utils, VipLocalStore} from '@modules'
import {DetailsSecondariesStore} from '@design-board/stores'
import {SidebarTableRowStore} from '@modules/drawer/stores/SidebarTableRowStore'
import {DetailsFieldType, DetailsSectionConfig, DetailsViewFieldConfig, FieldRendererType, FieldType, ICatalogFieldConfig, ICatalogFieldEditorConfig, nav, StoresInstance, WorkspaceConfig} from '@stores'
import {action, autorun, computed, observable, runInAction} from 'mobx'
import {useContext} from 'react'

export const FieldRendererContext = React.createContext<FieldRendererStore>(undefined)
export const useFieldRendererStore = () => useContext(FieldRendererContext)

export interface CatalogFieldRendererProps {
  tabId?: string,
  align?: SidebarAlign,
  field: Partial<DetailsViewFieldConfig>,
  workspace?: Partial<WorkspaceConfig>,
  section?: DetailsSectionConfig,
  className?: string,
  showLabel?: boolean,
  nestedData?: any,
  style?: React.CSSProperties
}

export class FieldRendererStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Partial<CatalogFieldRendererProps>, public otherStores : { cardFieldStore: BuilderCardFieldStore, detailsStore: ISidebarDetailsStore }) {
    super(stores)
    const {cardFieldStore} = otherStores
    if (cardFieldStore) {
      cardFieldStore.rendererStore = this
    }
  }

  get cardFieldStore() { return this.otherStores.cardFieldStore}

  get detailsStore() { return this.otherStores.detailsStore}

  get field() { return this.props.field}

  get section() { return this.props.section }

  get tabId() { return this.props.tabId}
  // @observable data: any
  @computed get data() {
    return this.cardFieldStore?.dataFor(this.section) ?? this.detailsStore?.data
  }

  registerEffects = () => {
    const _toDispose = [
      // autorun(() => {
      //   this.data = recursivelyRemovePrivateFields(_.cloneDeep(this.cardFieldStore?.dataFor(this.section) ?? this.detailsStore?.data))
      // }, {name: `Save detail store data on to field renderer store`}),

      // NVS - I'm turning off this code as it causes a slew of issues by modifying our data and screwing up our diff.  Computed fields need to be handled SERVERSIDE, not when any random client happens to read the data
      // See YFTS-7944 YFTS-8033 YFTS-6732 YFTS-8849
      // If you want client side computed fields, do them as an @computed value NOT as something that mutates our underlying database data
      /*autorun(() => {
        const {detailsStore, secondaries, isComputedField, data, type, computedValue, computedAdoptionCountAndBoolean, detailsStore: {adoptionStore, isOffline, saveOfflineChanges}, computedField, field} = this
        if (isComputedField && secondaries?.length && !field.agg) {

          const val = _.get(data, field.field)
          if (val !== computedValue) {
            runInAction(() => {
              _.set(data, field.field, computedValue)
              _.set(this.originalData, field.field, computedValue)
              isOffline ? detailsStore.isFirstRender && detailsStore.saveOfflineChanges() : detailsStore.isFirstRender && detailsStore.mutationSaveCatalogItemDetails()
            })
          }
        }
        // if (isComputedField && adoptionStore?.adoptions && field.agg && !field.onlyUIField) {
        //   _.get(data, field.field) != computedAdoptionCountAndBoolean && _.set(data, field.field, computedAdoptionCountAndBoolean)
        // }
      }, {name: 'Calculate computed values client side and save them back to the database'})*/
    ].filter(_.identity)

    return () => _toDispose.forEach(d => d())
  }

  @computed get isModal() {
    return this.detailsStore?.sidebar?.isModal === true
  }

  @computed get isComputedField() {
    return !!this.computedField
  }

  @computed get computedField() {
    const {field, catalogField} = this
    return field?.computed || catalogField?.computed
  }

  @computed get computedPathLoading() {
    const {computedField, detailsStore: {secondariesStore}} = this
    return computedField?.expression?.path && (!secondariesStore || secondariesStore.loading)
  }

  @computed get secondaries() {
    const {detailsStore, data, section} = this
    return section?.type === 'secondaries' ? detailsStore?.secondariesStore?.secondaries : (detailsStore?.adoptionStore?.adoptions ?? data?.secondaries ?? [])
  }

  @computed get computedValue() {
    const {data, secondaries, isComputedField, computedField} = this
    if (isComputedField) {
      const {expression: {field: f, fields, path, compute}} = computedField,
        d = path ? secondaries?.length ? secondaries : null : data
      if (compute && d) {
        return compute(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, f)) ? _.toNumber(_.get(v, f) ?? 0) : _.get(v, f)) : fields?.map(f => _.toNumber(_.get(d, f) ?? 0)))
      }
    }
    return null
  }

  @observable editableFieldText: { field: string, value, section?: DetailsSectionConfig } = null

  @action onBeginEditEditableField = () => {
    // console.debug(`onBeginEditEditableField`, {field})
    const {es, field: {field}, section, activeTabSection, cardFieldStore} = this,
      sectionId = section?.id

    this.editableFieldText = {
      field,
      value: sectionId && !cardFieldStore ? es.readField(activeTabSection?.dataById[sectionId], field).valAsString : _.get(this.data, field),
      section: {id: sectionId}
    }
  }

  @action onChangeEditableField = (val: string) => {
    // console.debug(`onChangeEditableField`, val)
    if (!this.editableFieldText) {
      this.onBeginEditEditableField()
    }
    const {editableFieldText} = this
    editableFieldText.value = val
  }

  @action commitEditableFieldChanges = () => {
    const {onChangeFieldValue, field, type: valueType, section} = this
    onChangeFieldValue({...field, valueType}, 'editableText', section)(this.editableFieldText.value)
    this.editableFieldText = null
  }

  @action onCancelEditableField = () => {
    // console.debug(`onCancelEditableField`)
    this.editableFieldText = null
    this.itemDetailsStore?.unfreeze()
  }

  @action onNumericValueChange = (field: string, section?: DetailsSectionConfig) => (val: number, valAsString: string) => {
    this.itemDetailsStore?.freeze()
    if (_.isNaN(val)) return
    if (!this.editableFieldText) {
      this.onBeginEditEditableField()
    }

    const {editableFieldText} = this
    editableFieldText.value = val

    this.updateFieldData(field, val, section, valAsString)
  }

  @action onInputKeyDown = (field: string, section?: DetailsSectionConfig) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    // console.log(`onKeyDown`, e.which)
    switch (e.which) {
      case Keys.ESCAPE: {
        this.onCancelEditableField()
        break
      }

      case Keys.ENTER: {
        this.onInputBlur(field, section)(e)
        break
      }
    }
  }

  @action onInputBlur = (field: string, section?: DetailsSectionConfig) => (e) => {
    // console.debug(`onInputBlur`, {field})
    if (this.editableFieldText && this.editableFieldText?.value !== null) {
      this.updateFieldData(field, this.editableFieldText.value, section)
    }
    this.itemDetailsStore?.unfreeze()
  }

  @action updateOnAssign = (fieldPath, value) => {
    // console.log(`updateOnAssign`, {fieldPath, value})

    const {sectionConfig, data: item, catalogField, currentStoreData} = this,
      {updateOnAssignment, section, field} = catalogField,

      cloneData = _.cloneDeep(currentStoreData)

    if (!_.isArray(updateOnAssignment)) {

      const {expression: {field: f, assignTo, computeField}} = updateOnAssignment,
        d = _.get(cloneData, section),
        fPath = computeField ?? field

      cloneData[section]?.some((sec) => {
        if (sectionConfig?.viewOptions && _.get(sec, sectionConfig.viewOptions?.field) === _.get(item, `${section}.${sectionConfig.viewOptions?.field}`)) {
          _.set(sec, fieldPath.replace(`${section}.`, ''), value)
          return true
        }
        return false
      })
      if (assignTo && d) {
        _.set(cloneData, f, assignTo(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, fPath)) ? _.toNumber(_.get(v, fPath) ?? 0) : _.get(v, fPath)) : 0))
      }
    }
    return cloneData
  }

  get currentStoreData() {
    return this.cardFieldStore?.data ?? this.detailsStore?.data
  }

  get sectionConfig() {
    const {field: {section}, catalog} = this
    return section ? catalog.sectionById[section] : null
  }

  @action updateFieldData = (fieldPath: string, val: any, section?: DetailsSectionConfig, valAsString?: string) => {
    const {catalog: {esFieldFor, catalogConfig}} = this,
      dragType = catalogConfig?.dragType

    let field = dragType?.field,
      {data, nestedField, nestedValue, itemDetailsStore, cardFieldStore, catalogField, currentStoreData} = this

    // console.log(`updateFieldData`, toJS({fieldPath, val, section, valAsString, data, nestedValue, nestedField}, {recurseEverything: true}), cardFieldStore)

    if (nestedValue && cardFieldStore?.cardStore?.props?.nested?.field) {
      const {cardStore: {props: {nested}}} = cardFieldStore,
        nestedField = esFieldFor(nested.field)
      if (fieldPath.startsWith(`${nestedField}.`)) {
        fieldPath = fieldPath.substring(nestedField.length + 1)
        data = nestedValue
      }
    }

    // handling case when field is not present
    // if (_.has(data, fieldPath) || isCreate || field == fieldPath) {
    if (section?.id && this.detailsStore?.updateSectionData) {
      this.detailsStore.updateSectionData(section, fieldPath, val)
    } else if (this.detailsStore?.updateFieldData) {
      this.detailsStore.updateFieldData(fieldPath, val, section, valAsString)
    } else {
      let path = section?.id ? fieldPath.substring(cardFieldStore.sectionConfig.path.length + 1) : fieldPath
      if (_.has(data, fieldPath) || itemDetailsStore?.isCreate || field == fieldPath || !_.has(data, fieldPath)) {
        if (_.get(data, fieldPath) != val) {
          if (catalogField?.updateOnAssignment) {
            _.merge(currentStoreData, this.updateOnAssign(fieldPath, val))
            return
          }
          _.set(data, fieldPath, val)
        } else {
          this.editableFieldText = {field: fieldPath, section, value: valAsString ?? val}
        }
      }
    }
  }

  // @action onChangeFieldValue = (fieldObj: DetailsViewFieldConfig, fieldType: FieldType) =>
  @action onChangeFieldValue = (fieldObj: any, fieldType: FieldType, section?: DetailsSectionConfig, valueAsString?: string) =>
    (e: string & React.ChangeEvent<HTMLInputElement>) => {
      // console.debug(`onChangeFieldValue`, {fieldObj, fieldType, section})
      const val = fieldObj.valueType == 'Float' ? parseFloat(e) : fieldObj.valueType == 'Int' ? parseInt(e) : e
      this.updateFieldData(fieldObj.field, fieldType === 'Boolean' || fieldType === 'headerCustomBoolean' ? e.target.checked : val, fieldObj.sectionId ?? section)
    }

  @computed get showFieldBorder(): boolean {
    const {isComputedField, editableFieldText, computedField} = this

    if (isComputedField) {
      const {expression: {field: f, fields}} = computedField
      if (f) {
        return f === editableFieldText?.field
      }
      if (fields?.length && editableFieldText?.field !== null) {
        return fields.includes(editableFieldText?.field)
      }
    }
    return false
  }

  @computed get originalData() {
    return this.cardFieldStore?.originalDataFor(this.section) ?? this.detailsStore?.originalData
  }

  get isValid() {
    return !this.detailsStore?.invalidFields || !this.detailsStore.invalidFields[this.field.field]
  }

  @computed get precisionFormatter() {
    const { catalogField } = this
    const precision = catalogField?.def?.precision ?? (catalogField?.def?.type == 'Int' ? 0 : 2)
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision })
  }

  @computed get catalogField(): ICatalogFieldConfig {
    const {catalog: {findFieldInTree, fieldByWorkspaceIndex, allWorkspaceFields}, detailsStore, field} = this

    if (field.field) {
      let v =  fieldByWorkspaceIndex[detailsStore.index]?.fields[field.field]

      if (v) return v

      v = allWorkspaceFields[field.field]

      if (v) return v

      const fk = findFieldInTree(field.field, true)
      if (fk) {
        const otherWorkspace = fieldByWorkspaceIndex[fk.esIndex]
        if (otherWorkspace) {
          return otherWorkspace.fields[field.field.substring(fk.field.length + 1)]
        }
      }
    }

    return null
  }

  @computed get editor(): ICatalogFieldEditorConfig {
    return this.catalogField?.editor ?? {}
  }

  get type() { return this.field?.def?.type ?? this.field?.type ?? this.catalogField?.def?.type}

  get def() { return this.field?.def ?? this.catalogField?.def}

  @computed get fieldLabel() {
    const {field, catalogField, catalog} = this
    return field?.label ?? (catalogField ? catalog.labelForField(catalogField) : null)
  }

  @computed get label() {
    if (this.cardFieldStore) return this.cardFieldStore.title

    const {catalog, field, catalogField, data, originalData, section, renderer, detailsStore: {isValidForUpload, selectedDetailTab, detailDef}} = this
    if (renderer == 'sync' && !isValidForUpload) return null
    if (field.header?.field && section) {
      const d = _.get(data ?? originalData, field.header?.path),
        sectionData = _.find(d, {exId: section?.id})

      return _.get(sectionData ?? d, field.header?.field)
    }

    if (section && selectedDetailTab?.itemSource && catalogField?.def?.type !== 'Boolean') {
      const d = _.get(data, selectedDetailTab?.itemSource),
        sectionData = _.find(d, {exId: section?.id})

      return _.get(sectionData ?? d, field.field)
    }

    const ws = catalog.workspaceByIndex[detailDef.id]

    return field.label === null ? null : field.label ?? catalogField?.label ?? catalogField?.def?.label
                                         ?? catalogField?.def?.displayName?.singular
                                         // Todo this linear search is expensive and should be a dictionary - to do so we would need to wrap all workspace configs with a concrete class
                                         // that gives us such functionality
                                         ?? catalog.labelForField(ws?.catalog?.fields?.find(f => f.field == field.field), null)
  }

  get dataMap() {
    return {...dataMapperConfig.specDataMapperConfig, ...(this.catalogField?.editor?.dataMap || {})}
  }

  @computed get originalValue() {
    return this._valueFor(this.originalData)
  }

  _valueFor = (data: any) => {
    const {es, editableFieldText, field, section, activeTabSection, catalogField, catalog} = this
    const parsed = (!field?.field
                    ? es.readField(data, field?.nested?.field) ?? {val: null, valAsString: null}
                    : !section?.id ? es.readField(data, field.field, typeof catalogField?.filter === 'boolean' ? null : catalogField?.filter?.subField)
                                   : editableFieldText?.section?.id === section?.id && editableFieldText?.field === field?.field
                                     ? editableFieldText?.value
                                     : activeTabSection
                                       ? es.readField(activeTabSection?.dataById[section?.id], field.field, typeof catalogField?.filter === 'boolean' ? null : catalogField?.filter?.subField)
                                       : es.readField(data, field.field, typeof catalogField?.filter === 'boolean' ? null : catalogField?.filter?.subField))
                   ?? {val: null, valAsString: null}

    // console.log({field, section, parsed, data: toJS(data), activeTabSection: toJS(activeTabSection)})
    let r = {
      ...(parsed ?? {}),
      valAsString: catalogField?.editor?.localeStringOptions && parsed.val ? parsed?.val.toLocaleString(undefined, catalogField?.editor?.localeStringOptions) : parsed.valAsString
    }

    if (_.isArray(r.val)) {
      r.val = r.val.filter(_.identity)
    }
    return r
  }
  @computed get value(): { valAsString?: string, val? } {
    return this._valueFor(this.data)
  }

  @computed get align() { return this?.detailsStore?.sidebar?.align ?? 'inline'}

  @computed get isDirty() {
    return this.itemDetailsStore?.isDirty || (this.editableFieldText != null && this.editableFieldText?.value != this.value?.val)
  }

  // @computed get isDirty() {
  //   return false
  //   const {detailsStore, itemDetailsStore, originalData, field: {field}, value} = this
  //   return itemDetailsStore?.isDirty || (detailsStore?.isDirty)&& _.get(originalData, field) != value?.val
  // }

  @computed get selectedValues() {
    const {value: {val, valAsString}} = this
    // in case of attributes.GNT_MPV_CollectionLayer_Enum, selectedValues is a string
    return new Set<string>(_.isArray(val) ? val.filter(_.identity).map(v => v.id || v._id) : valAsString ? [valAsString] : [])
  }

  @observable showNested = new Map<string, boolean>()
  @action toggleShowNested = (key) => this.showNested.set(key, !this.showNested.get(key))

  get nestedData() { return this.props.nestedData}

  @computed get nestedValue() {
    if (this.nestedData) { return this.nestedData}

    const {es, field: {nested}, value: {val}} = this
    if (nested?.field) {
      const {val: nestedValue} = es.readField(val, nested.field)
      return nestedValue
    }
    return null
  }

  @computed get nestedDataMap() {
    return {...dataMapperConfig.specDataMapperConfig, ...(this.nestedLibrary?.dataMap || {})}
  }

  @computed get nestedBoardField() {
    return this.nestedField ? this.catalog.fieldByName(this.nestedField) : null
  }

  @computed get nestedField(): string {
    const {field: {field, nested}} = this
    return nested ? `${field}.${nested.field}` : null

  }

  @computed get nestedLibrary() {
    const {catalog: {libraries}, nestedField} = this

    return nestedField && libraries ? libraries.find(l => l.catalogFields?.indexOf(nestedField) != -1) : null
  }

  @computed get library() {
    const {catalog: {libraries}, field: {field, nested}} = this
    return nested && libraries ? libraries.find(l => l.name === nested?.library) : null
  }

  @action toggleNestedLibraryDetails = (id, align: SidebarAlign, target: 'root' | 'nested') => {
    const {nestedLibrary, field, catalogField, detailsStore: {sidebar: sidebarStore}} = this,
      {open, path, selectedPathItem, tab} = sidebarStore,
      targetLibrary = target === 'nested' ? nestedLibrary.name : (field.library ?? catalogField.esIndex ?? field.field)

    if (tab !== 1 && open && selectedPathItem?.libraryId == targetLibrary && selectedPathItem?.where?.length && _.first<ISidebarWhereClause>(selectedPathItem.where).value == id) {
      sidebarStore.open = false
    } else {
      if (align === 'left' && tab === 1) {
        sidebarStore.tab = 0
      }
      sidebarStore.updateState({open: true, path: [...path, {libraryId: targetLibrary, where: [{value: id}], sidebarContext: 'xref', xref: align == 'left' || nestedLibrary ? null : field.field}]})
    }
  }

  get itemDetailsStore() {
    return this.cardFieldStore?.cardStore?.detailsStore ?? this.detailsStore?.itemDetailsStore
  }

  updateEnumValues = (field, value) => {
    const {section, detailsStore, itemDetailsStore} = this;

    (detailsStore.updateFieldData ?? itemDetailsStore.updateFieldData)(field, value, section)
  }

  updateLookupItems = (field, value) => {
    console.log(field, value)
    const {section, detailsStore, itemDetailsStore} = this;

    (detailsStore.updateFieldData ?? itemDetailsStore.updateFieldData)(field, value, section)
  }

  @observable showPopover = false

  @action setShowPopoverOnKeydown = (e) => {
    e.stopPropagation()
    if (e.which === Keys.ESCAPE) {
      e.preventDefault()
      let element = document.getElementById(this.catalogField?.field)
      element?.focus()
      this.showPopover = false
    }
  }

  @action setShowPopover = (popoverState) => {
    this.showPopover = popoverState
  }

  @action onFocusOut = (e) => {
    this.showPopover && (this.showPopover = false)
  }

  @action togglePopover = () => {
    this.showPopover = !this.showPopover
  }

  @computed get nestedLabel() {
    const {field: {nested, field}, catalog} = this
    if (nested?.field) {
      return nested.label ?? catalog.labelForFieldName_currentCatalog(`${field}.${nested.field}`, /*nestedValue?.length > 1 ? 'plural' :*/ 'plural')
    }
    return null
  }

  @observable.ref ref: HTMLDivElement
  setRef = ref => this.ref = ref

  @action onKeyPressNavigation = (e) => {
    e.stopPropagation()
    if (e.which === Keys.ESCAPE) {
      e.preventDefault()
      let element = document.getElementById(this.catalogField.esIndex)
      element?.focus()
    }
  }

  get isNumericField() {
    const {type} = this
    return _.includes(['Int', 'Float', 'Double'], type)
  }

  @computed get renderer(): FieldRendererType | CustomAttributeType | DetailsFieldType {
    const {catalogField, type} = this
    const r = catalogField?.renderer ?? type

    if (catalogField?.renderer === 'html') return 'html'

    switch (r) {
      case 'Enum':
        return 'xref'

      case 'Int':
      case 'Float':
        return 'numeric'

      case 'String':
        return 'text'

      default:
        break
    }

    if (r) { return r }

    const {value} = this

    if (_.isBoolean(value.val)) {
      return 'Boolean'
    }

    if (catalogField?.esIndex) {
      return 'specCard'
    }

    //console.warn(`Unknown renderer type for '${field.field}' - Value is '${value.val}'`)
    return 'text'
  }

  get isSearchResult() {
    const {detailsStore: {searchResults}, field} = this
    return (searchResults ?? []).find(f => f.type == 'field' && f.id == field.field) != null
  }

  get isCurrentSearchResult() {
    const {detailsStore: {searchResults, isSearching, searchTabIndex}, field, isSearchResult} = this
    return isSearching && isSearchResult && searchResults && searchResults[searchTabIndex]?.id == field.field
  }

  @observable imageLoadError = false
  @action onImageLoadError = (e: React.UIEvent<HTMLImageElement>) => {
    this.imageLoadError = true

    if (e.currentTarget.src.indexOf('TokenURL=centric') != -1) {
      console.warn(`Error loading C8 image - possible token expiration`)
      if (!lockr.get('SecurityTokenURL') && !DEBUG) {
        nav.routeToLogoutPage()
      }
    }
  }

  @computed get requiredParentField() {
    const {field: {field}, catalog} = this,
      {_fieldByName} = catalog

    let path = field ? field.substring(0, field.lastIndexOf('.')) : null

    while (path) {
      if (_fieldByName[path]?.esIndex) {
        return _fieldByName[path]
      }
      path = path.substring(0, path.lastIndexOf('.'))
    }

    return null
  }

  @computed get parentObject() {
    // Require both source and destination
    const {requiredParentField, data, originalData} = this
    if (requiredParentField) {
      const originalSpec = _.get(originalData, requiredParentField.field),
        currentSpec = _.get(data, requiredParentField.field)

      const r = !originalSpec || !currentSpec || !_.isEqual(originalSpec?.id, currentSpec?.id) ? null : currentSpec
      //console.log(r, requiredParentField.field, originalSpec, currentSpec, originalSpec != currentSpec, !_.isEqual(originalSpec, currentSpec))
      return r
    }
  }

  @computed get editable() {
    const {catalog, field, catalogField, section, secondaries, cardFieldStore} = this,
      {stores, stores: {users: { current }, users}, workspaceConfig} = catalog

    const editable = field.editable ?? catalogField?.editable
    if (_.isFunction(editable)) {
      const secondary = Array.isArray(secondaries) ? _.find(secondaries, (secondary) => section?.id == secondary?.id || secondary?.exId) || {} : secondaries
      return editable({
        data: cardFieldStore?.cardStore?.data ?? this.data,
        user: users._currentUserAndRoles, stores,
        workspace: workspaceConfig, secondary,
        field: this.field,
        isPublished: this.cardFieldStore ? !this.cardFieldStore?.cardStore?.isOffline : this.detailsStore ? this.detailsStore?.isPublished : false
      })
    } else if (field.field && field.editable !== false) {
      return editable === true || catalog.isFieldEditable(field.field, () => this.data, null)
    }

    return false
  }

  @computed get readOnly() {
    const {requiredParentField, parentObject, isComputedField, editable} = this
    return isComputedField || editable === false || (requiredParentField && !parentObject)
  }

  @computed get currencyIcon() {
    const {catalogField} = this
    return currencyToSymbolMap[_.toUpper(catalogField?.def?.currency)]
  }

  @computed get computedAdoptionCountAndBoolean() {
    const {detailsStore: {adoptionStore}, isComputedField, renderer, computedField} = this
    if (isComputedField && adoptionStore?.adoptions) {
      const {expression: {field: f, fields, compute}} = computedField,
        {adoptions: d} = adoptionStore
      return compute && d ? renderer === 'Boolean' ? compute(Array.isArray(d) ? d.length === 0 ? [] : d?.map(v => _.get(v, f) ?? 0) : fields?.map(f => _.get(d, f) ?? false))
                                                   : compute(Array.isArray(d) ? d.length === 0 ? [] : d?.map(v => _.toNumber(_.get(v, f) ?? 0)) : fields?.map(f => _.toNumber(_.get(d, f) ?? 0))) : 0
    }
    return 0
  }

  @computed get activeTabSection() {
    const {detailsStore: {storeMap, activeTabStore, sidebar}, section} = this
    return (sidebar?.type !== 'modal')
           ? storeMap ? storeMap[section?.type] : null
           : activeTabStore
  }

  @computed get fieldRenderer() {
    const {editor, type, catalogField, value, field: {field}} = this
    const r = editor?.fieldRenderer ?? (type == 'LookupItem' || type == 'LookupItemList' || type == 'Enum' || type == 'EnumList' ? 'dropdown' : undefined)

    //console.log({field, fieldDef: this.field, value, type, catalogField, editor, r})
    return r
  }

  get searchWords() {
    return this.detailsStore?.sidebar?.searchWords ?? []
  }

  @action onChangeSpec = (d, path) => {
    this.itemDetailsStore.updateFieldData(path, d)
    const {itemDetailsStore: {constrainedFields}} = this,
      affectedField = constrainedFields.find(f => f.relatedField?.field == path)
    if (this.itemDetailsStore) {
      const {isConstraintDirty, _assignConstrainedValues} = this.itemDetailsStore
      if (!isConstraintDirty) {
        _assignConstrainedValues(!isConstraintDirty)
      }
    }
    if (affectedField && affectedField?.field !== path) {
      _.set(this.data, affectedField.field, null)
    }
  }

  @action deleteSpec = (path) => {
    const value = _.get(this.data, path)
    _.set(this.data, path, Array.isArray(value) ? [] : null)
    const {catalogField, itemDetailsStore: {constrainedFields}} = this,
      affectedField = constrainedFields.find(f => f.relatedField?.field == path)

    if (affectedField) {
      _.set(this.data, affectedField.field, null)
    }

    if (catalogField.onAssign) {
      catalogField.onAssign({field: catalogField, mutations: {}, data: this.data, libraryItem: null, stores: this.stores})
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/CatalogFieldRendererStore.tsx